import api from './api';

// DVM Extractor (Apps → DVM Extractor). Superadmin-only; the lambda enforces that as well, since hiding
// a card does not stop anyone calling the endpoint.
//
// EXTRACT IS A JOB, NOT A REQUEST. A property's DVM is one OHIP call per conversion code — minutes of
// work — and API Gateway abandons any request at ~29 seconds. `extract` returns a run id straight away
// and `pollRun` waits on the run row.
const dvmExtractorService = {
    getAll: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.hotel_id) params.append('hotel_id', filters.hotel_id);
        const qs = params.toString();
        const res = await api.get(qs ? `/dvm-extractors?${qs}` : '/dvm-extractors');
        return res.data;
    },

    getById: async (id) => (await api.get(`/dvm-extractors/${id}`)).data,

    create: async (data) => (await api.post('/dvm-extractors', data)).data,

    update: async (id, data) => (await api.put(`/dvm-extractors/${id}`, data)).data,

    delete: async (id) => (await api.delete(`/dvm-extractors/${id}`)).data,

    extract: async (id, format) => (await api.post(`/dvm-extractors/${id}/extract`, format ? { format } : {})).data,

    getRuns: async (id) => (await api.get(`/dvm-extractors/${id}/runs`)).data,

    getRun: async (runId) => (await api.get(`/dvm-extractors/runs/${runId}`)).data,

    getRunFile: async (runId) => (await api.get(`/dvm-extractors/runs/${runId}/file`)).data,

    // Wait for a run to finish. Two minutes of polling at 3s, then it gives up watching — the run itself
    // carries on and the history list will show how it ended, so a slow extract is never lost, only
    // unwatched.
    pollRun: async (runId, { intervalMs = 3000, timeoutMs = 600000, onTick } = {}) => {
        const deadline = Date.now() + timeoutMs;
        for (;;) {
            await new Promise(r => setTimeout(r, intervalMs));
            const { run } = await dvmExtractorService.getRun(runId);
            if (onTick) onTick(run);
            if (run.status === 'done' || run.status === 'error') return run;
            if (Date.now() > deadline) throw new Error('The extract is still running — check the run history in a moment.');
        }
    },

    // The file comes back base64 (the run row holds it as text), so the download is built here rather
    // than from a URL. Same path for every format; only the media type differs.
    download: async (runId) => {
        const file = await dvmExtractorService.getRunFile(runId);
        const bytes = Uint8Array.from(atob(file.base64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: file.mediaType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName || `dvm-extract-${runId}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return file;
    },
};

export default dvmExtractorService;
